/** Shows the sign-in screen instead of a screen that needs a session.
 *
 * **Not a route guard, deliberately.** A guard in the router would be a second place that decides who
 * may see what, and the first place — the API, where `deps.scoped_session` sets the tenant scope from
 * the resolved session — is the one that cannot be bypassed by editing JavaScript in a console. This
 * component decides what to *show*; the server decides what may be *read*. If they ever disagree, the
 * server wins and the screen is simply empty, which is the failure mode to prefer.
 *
 * It renders the empty shell frame (rail and bar, no text) while the first `/auth/me` is in flight. A
 * sign-in form that flashed on every load for someone who is signed in would look like a bug every
 * time, and a spinner would be worse: the check takes one request and is usually cached. The frame
 * replaces the blank page this used to leave, which was white for as long as the request took.
 *
 * **A failed check is not a signed-out one** — EXP-29's "return after interruption", and the same
 * defect `api/client.ts` describes one level down. `useMe` answers `null` for a 401, which is the
 * question being answered; it *throws* for a 500 or a dropped connection, and this component used
 * to render the sign-in form for both. So somebody whose API had stopped was told to sign in, and
 * signing in failed the same way, with nothing on screen saying why. That is the wrong remedy
 * presented as the only one — which is the exact sentence that file uses about a 401 rendering as
 * "could not be loaded".
 */

import type { ReactNode } from "react";

import { useMe } from "../api/auth";
import { AuthCard, AuthPage } from "../components/AuthLayout";
import { ReadFailed } from "../components/Loaded";
import { ReaderLocaleScope } from "../components/ReaderLocaleScope";
import { SignInScreen } from "../screens/SignIn";
import { BootFrame } from "./BootFrame";

export function RequireSession({ children }: { children: ReactNode }) {
  const { data: me, isPending, error, refetch } = useMe();

  // The frame, not a blank page: see `BootFrame`.
  if (isPending) return <BootFrame />;
  // Before the `!me` branch, because `data` is `undefined` on a failure too and the two are
  // opposite instructions: one says sign in, the other says the server could not be reached.
  if (error) {
    return (
      <AuthPage>
        <AuthCard title="EmitIQ could not check your session" stage="session-unreadable">
          <ReadFailed of="Your session" error={error} onRetry={() => void refetch()} />
        </AuthCard>
      </AuthPage>
    );
  }
  // `inPlace`: this form stands where the screen was, so after a session that ended it can simply
  // let the screen back in — see `SignInScreen` and `api/sessionEnded.ts`.
  if (!me) return <SignInScreen inPlace />;
  return <ReaderLocaleScope locale={me.locale}>{children}</ReaderLocaleScope>;
}
